import { Table, Spin, Empty, Pagination } from "antd";
import "./CustomDataTable.css";
import PropTypes from "prop-types";

/**
 * COMPONENTE DATATABLE REUTILIZABLE:
 * Tabla genérica con paginación, ordenamiento y personalización
 *
 * @param {Array} data - Datos a mostrar en la tabla
 * @param {Array} columns - Configuración de columnas (formato Ant Design)
 * @param {boolean} loading - Estado de carga
 * @param {Function} onRowClick - Callback al hacer click en una fila
 * @param {Object} selectedRow - Fila actualmente seleccionada
 * @param {boolean} showPagination - Mostrar/ocultar paginación
 * @param {number} pageSize - Tamaño de página por defecto
 * @param {string} emptyText - Texto cuando no hay datos
 * @param {string} className - Clases CSS adicionales
 * @param {Object} ...tableProps - Props adicionales de Ant Design Table
 */

export const CustomDataTable = ({
  data = [],
  columns = [],
  loading = false,
  onRowClick = null,
  selectedRow = null,
  showPagination = true,
  emptyText = "No hay datos disponibles",
  className = "",
  pagination,
  onPageChange,
  ...tableProps
}) => {
  //* HANDLERS
  /**
   * Maneja el click en una fila
   */
  const handleRowClick = (record) => {
    if (onRowClick) {
      onRowClick(record);
    }
  };

  /**
   * Configura props de cada fila (estilos, eventos)
   */
  const rowConfig = (record) => ({
    onClick: () => handleRowClick(record),
    className: `data-table-row ${
      selectedRow && selectedRow.id === record.id ? "data-table-row-selected" : ""
    } ${onRowClick ? "data-table-row-clickable" : ""}`,
  });

  //* RENDER
  return (
    <div className={`data-table-container ${className}`}>
      {/* Tabla */}
      <Table
        dataSource={data}
        columns={columns}
        loading={{
          spinning: loading,
          indicator: <Spin size="large" />,
        }}
        locale={{
          emptyText: <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
        }}
        pagination={false}
        rowKey="id"
        onRow={rowConfig}
        scroll={{ x: "max-content" }}
        {...tableProps}
      />

      {/* Paginación personalizada */}
      {showPagination && pagination && (
        <div className="data-table-pagination">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={onPageChange}
            showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} productos`}
            locale={{
              items_per_page: "/ página",
              jump_to: "Ir a",
              jump_to_confirm: "confirmar",
              page: "Página",
              prev_page: "Página anterior",
              next_page: "Página siguiente",
              prev_5: "5 páginas previas",
              next_5: "5 páginas siguientes",
              prev_3: "3 páginas previas",
              next_3: "3 páginas siguientes",
            }}
          />
        </div>
      )}
    </div>
  );
};

CustomDataTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
  selectedRow: PropTypes.object,
  showPagination: PropTypes.bool,
  emptyText: PropTypes.string,
  className: PropTypes.string,
  pagination: PropTypes.object,
  onPageChange: PropTypes.func,
};
